import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useTranslation } from 'react-i18next';

const RequestDetailModal = ({ request, onClose, onApprove, onReject }) => {
  const { t } = useTranslation();
  const [buyer, setBuyer] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDetails();
  }, [request]);

  const loadDetails = async () => {
    try {
      setLoading(true);
      
      // Load buyer info
      if (request.buyerId) {
        const buyerRef = doc(db, 'users', request.buyerId);
        const buyerSnap = await getDoc(buyerRef);
        if (buyerSnap.exists()) {
          setBuyer({ id: buyerSnap.id, ...buyerSnap.data() });
        }
      }
      
      // Load product info
      if (request.productId) {
        const productRef = doc(db, 'products', request.productId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          setProduct({ id: productSnap.id, ...productSnap.data() });
        }
      }
    } catch (error) {
      console.error('Error loading request details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      scheduled: 'bg-green-100 text-green-800',
      active: 'bg-purple-100 text-purple-800',
      completed: 'bg-gray-100 text-gray-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    const labels = {
      pending: t('videoCall.pending'),
      approved: t('videoCall.approved'),
      scheduled: t('videoCall.scheduled'),
      active: t('videoCall.active'),
      completed: t('videoCall.completed'),
      rejected: t('videoCall.rejected'),
      cancelled: t('videoCall.cancelled')
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badges[status] || badges.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">{t('videoCall.requestDetails')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('videoCall.status')}</label>
            {getStatusBadge(request.status)}
          </div>

          {/* Buyer Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.buyer')}</label>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-semibold text-gray-800">
                {buyer?.displayName || buyer?.name || buyer?.email || 'N/A'}
              </p>
              {buyer?.email && (
                <p className="text-sm text-gray-600 mt-1">{buyer.email}</p>
              )}
              {buyer?.contact?.phone && (
                <p className="text-sm text-gray-600">{buyer.contact.phone}</p>
              )}
            </div>
          </div>

          {/* Product Info */}
          {product && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.product')}</label>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-800">{product.name}</p>
                {product.price && (
                  <p className="text-sm text-gray-600 mt-1">฿{product.price.toLocaleString()}</p>
                )}
                {product.quantity && (
                  <p className="text-sm text-gray-600">จำนวน: {product.quantity} {product.unit || ''}</p>
                )}
              </div>
            </div>
          )}

          {/* Time Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('videoCall.time')}</label>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div>
                <span className="text-sm text-gray-600">{t('videoCall.requestedTime')}:</span>
                <p className="font-semibold text-gray-800">{formatTime(request.requestedTime)}</p>
              </div>
              {request.scheduledTime && (
                <div>
                  <span className="text-sm text-gray-600">{t('videoCall.scheduledTime')}:</span>
                  <p className="font-semibold text-green-600">{formatTime(request.scheduledTime)}</p>
                </div>
              )}
              {request.duration && (
                <div>
                  <span className="text-sm text-gray-600">{t('videoCall.duration')}:</span>
                  <p className="font-semibold text-gray-800">{request.duration} {t('videoCall.minutes')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Rejection Reason */}
          {request.status === 'rejected' && request.rejectionReason && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('videoCall.rejectionReason')}</label>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-red-800">{request.rejectionReason}</p>
              </div>
            </div>
          )}

          {/* Suggested Times */}
          {request.suggestedTimes && request.suggestedTimes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('videoCall.suggestedTimes')}</label>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <ul className="space-y-2">
                  {request.suggestedTimes.map((time, index) => (
                    <li key={index} className="text-blue-800">
                      • {formatTime(time)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('videoCall.timestamps')}</label>
            <div className="bg-gray-50 p-4 rounded-lg space-y-1 text-sm">
              <p className="text-gray-600">
                {t('videoCall.sentAt')}: <span className="text-gray-800">{formatTime(request.createdAt)}</span>
              </p>
              {request.approvedAt && (
                <p className="text-gray-600">
                  {t('videoCall.approvedAt')}: <span className="text-gray-800">{formatTime(request.approvedAt)}</span>
                </p>
              )}
              {request.rejectedAt && (
                <p className="text-gray-600">
                  {t('videoCall.rejectedAt')}: <span className="text-gray-800">{formatTime(request.rejectedAt)}</span>
                </p>
              )}
              {request.startedAt && (
                <p className="text-gray-600">
                  {t('videoCall.startedAt')}: <span className="text-gray-800">{formatTime(request.startedAt)}</span>
                </p>
              )}
              {request.endedAt && (
                <p className="text-gray-600">
                  {t('videoCall.endedAt')}: <span className="text-gray-800">{formatTime(request.endedAt)}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        {request.status === 'pending' && (
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-end gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common.close')}
            </button>
            <button
              onClick={() => {
                const reason = prompt(t('videoCall.rejectionReasonPrompt'));
                if (reason) onReject(reason);
              }}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
            >
              ❌ {t('videoCall.reject')}
            </button>
            <button
              onClick={onApprove}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
            >
              ✅ {t('videoCall.approve')}
            </button>
          </div>
        )}

        {request.status === 'scheduled' && (
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-end gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common.close')}
            </button>
            <button
              onClick={() => {
                window.location.href = `/marketplace/video-call/${request.chatId || request.id}`;
              }}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
            >
              📹 {t('videoCall.start')}
            </button>
          </div>
        )}

        {request.status !== 'pending' && request.status !== 'scheduled' && (
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestDetailModal;

